import {
  DeepPartial,
  IFirestoreWhere,
  IFirestoreOrderBy,
  ValueOf,
} from '../types';
import {
  FirestoreError,
  DocumentReference,
  CollectionReference,
  Query,
  FieldPath,
  OrderByDirection,
  QuerySnapshot,
  DocumentSnapshot,
  SetOptions,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  startAfter,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';

export const getData = <T>(documentRef: DocumentReference) => {
  return getDoc(documentRef).then((resp) => resp.data()) as Promise<T>;
};

export const getDataFromCollectionWhere = async <T = unknown>(
  collectionRef: CollectionReference,
  orderByClause: {
    fieldPath: string | FieldPath;
    direction: OrderByDirection;
  },
  firstWhere?: IFirestoreWhere,
  secondWhere?: IFirestoreWhere,
) => {
  let firestoreQuery: Query = collectionRef;

  if (firstWhere) {
    const { fieldPath, opStr, value } = firstWhere;
    firestoreQuery = query(firestoreQuery, where(fieldPath, opStr, value));
  }

  if (secondWhere) {
    const { fieldPath, opStr, value } = secondWhere;
    firestoreQuery = query(firestoreQuery, where(fieldPath, opStr, value));
  }

  firestoreQuery = query(
    firestoreQuery,
    orderBy(orderByClause.fieldPath, orderByClause.direction),
    limit(1),
  );

  return getDocs(firestoreQuery).then((resp) => {
    let result: T | null = null;
    if (!resp.empty) {
      result = resp.docs[0].data() as T;
    }
    return result;
  });
};

export const getCollection = <T>(collectionRef: CollectionReference) => {
  return getDocs(collectionRef).then((resp) => {
    const result: T[] = [];
    resp.forEach((doc) => result.push(doc.data() as T));
    return result;
  });
};

export const getCollectionSnapshot = <T>(
  collectionRef: CollectionReference,
  listener: (data: T[], snapshot?: QuerySnapshot) => void,
) => {
  return onSnapshot(collectionRef, (querySnapshot) => {
    const result: T[] = [];
    querySnapshot.forEach((doc) => result.push(doc.data() as T));
    return listener(result, querySnapshot);
  });
};

export const getCollectionCursor =
  <T>(
    collectionRef: CollectionReference,
    orderByClause: IFirestoreOrderBy,
    limitValue: number,
  ) =>
  (startAfterValue: ValueOf<T>) => {
    if (!startAfterValue) return;

    const firestoreQuery = query(
      collectionRef,
      orderBy(orderByClause.fieldPath, orderByClause.direction),
      startAfter(startAfterValue),
      limit(limitValue),
    );

    return getDocs(firestoreQuery).then((querySnapshot) => {
      return querySnapshot.docs.map((doc) => doc.data() as T);
    });
  };

export const getCollectionSnapshotWhere = <T>(
  collectionRef: CollectionReference,
  {
    limit: limitValue,
    orderBy: orderByClause,
  }: { limit?: number; orderBy: IFirestoreOrderBy },
  listener: (data: T[], snapshot?: QuerySnapshot) => void,
) => {
  let firestoreQuery: Query = collectionRef;

  if (orderByClause) {
    firestoreQuery = query(
      firestoreQuery,
      orderBy(orderByClause.fieldPath, orderByClause.direction),
    );
  }

  if (limitValue) {
    firestoreQuery = query(firestoreQuery, limit(limitValue));
  }

  return onSnapshot(firestoreQuery, (querySnapshot) => {
    const result: T[] = [];
    querySnapshot.forEach((doc) => result.push(doc.data() as T));
    return listener(result, querySnapshot);
  });
};

export const getCollectionsByFilter = <T>(
  collectionRef: CollectionReference,
  config?: {
    limit?: number;
    orderBy?: {
      fieldPath: string | FieldPath;
      direction: OrderByDirection;
    };
    firstWhere?: IFirestoreWhere;
    secondWhere?: IFirestoreWhere;
  },
) => {
  const {
    limit: limitValue,
    firstWhere,
    orderBy: orderByClause,
    secondWhere,
  } = config || {};
  let firestoreQuery: Query = collectionRef;

  if (firstWhere) {
    const { fieldPath, opStr, value } = firstWhere;
    firestoreQuery = query(firestoreQuery, where(fieldPath, opStr, value));
  }

  if (secondWhere) {
    const { fieldPath, opStr, value } = secondWhere;
    firestoreQuery = query(firestoreQuery, where(fieldPath, opStr, value));
  }

  if (orderByClause) {
    firestoreQuery = query(
      firestoreQuery,
      orderBy(orderByClause.fieldPath, orderByClause.direction),
    );
  }

  if (limitValue) {
    firestoreQuery = query(firestoreQuery, limit(limitValue));
  }

  return getDocs(firestoreQuery).then((resp) => {
    const result: T[] = [];
    resp.forEach((doc) => result.push(doc.data() as T));
    return result;
  });
};

export const setData = <T extends object>(
  documentRef: DocumentReference,
  data: T,
  options?: SetOptions,
) => {
  // changes any undefined values to null (makes firebase happy)
  Object.keys(data as Record<string, unknown>).forEach((key) => {
    const value = (data as Record<string, unknown>)[key];
    if (value === undefined) (data as Record<string, unknown>)[key] = null;
  });

  if (options) {
    return setDoc(documentRef, data as Record<string, unknown>, options);
  }

  return setDoc(documentRef, data as Record<string, unknown>);
};

export const updateOrCreateData = <T>(
  documentRef: DocumentReference,
  data: DeepPartial<T>,
) => {
  return setDoc(documentRef as DocumentReference<T>, data as any, {
    merge: true,
  });
};

export const deleteDocument = (documentRef: DocumentReference) => {
  return deleteDoc(documentRef);
};

export const getSnapshot = <T>(
  documentRef: DocumentReference,
  listener: (data: T, snapshot?: DocumentSnapshot) => void,
  onError?: (error: FirestoreError) => void,
) => {
  return onSnapshot(
    documentRef,
    (snap) => listener(snap.data() as T, snap),
    onError,
  );
};
